import { lazy, Suspense, useState } from 'react';
import { CalendarRange, ChevronLeft, ChevronRight } from 'lucide-react';
import { businessDate, monthOf } from '@domain/dates';
import { buttonVariants } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

/** The grid is loaded the first time a picker opens, as the date picker's calendar is: it brings date-fns. */
const MonthGrid = lazy(() => import('./month-grid').then((m) => ({ default: m.MonthGrid })));

/**
 * The app's month picker (2026-09-27, Finance Task 16): the date picker's pattern, a Radix Popover holding a grid of
 * the twelve months of one year in Vietnamese, with the year stepped by "Năm trước"/"Năm sau". The value is the
 * API's `YYYY-MM` in and out (`''` for none); `onChange` fires once per month chosen, and only when it changed.
 * "Tháng này" is the business month in Ho Chi Minh City (`businessDate`), whatever the laptop's zone.
 *
 * The keyboard: the grid is one tab stop; ArrowLeft/ArrowRight move a month, ArrowUp/ArrowDown three (a row),
 * Home/End the row's ends, crossing a year's edge moves the grid to that year, and a move past `min`/`max` stops on
 * the last month offered. Enter or Space chooses; Escape closes and gives the focus back to the trigger.
 */

export type MonthPickerProps = {
  /** What the month is for; with the value it names the trigger ("Tháng bắt đầu: 03/2026"). */
  label: string;
  value: string;
  onChange: (value: string) => void;
  /** 'YYYY-MM', both ends included. */
  min?: string;
  max?: string;
  /** A required month offers no "Xoá". */
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  /** A settled month: focusable and announced (`aria-readonly`), shown muted, and the popover never opens. */
  readOnly?: boolean;
  id?: string;
  className?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  align?: 'start' | 'center' | 'end';
};

const MONTH = /^(\d{4})-(0[1-9]|1[0-2])$/;
const pad = (n: number, width = 2) => String(n).padStart(width, '0');
const key = (year: number, month: number) => `${pad(year, 4)}-${pad(month)}`;
const parts = (month: string): [number, number] => [Number(month.slice(0, 4)), Number(month.slice(5, 7))];
/** A month moved by `delta` months, across years. */
const shift = (month: string, delta: number): string => {
  const [y, m] = parts(month);
  const index = y * 12 + (m - 1) + delta;
  return key(Math.floor(index / 12), (index % 12) + 1);
};
/** MM/YYYY, the company's reading of a month; `null` when the value is not one. */
const shown = (value: string): string | null => {
  const m = MONTH.exec(value);
  return m === null ? null : `${m[2]}/${m[1]}`;
};

const NAV = cn(buttonVariants({ variant: 'ghost', size: 'icon-sm' }), 'pointer-fine:min-h-0 pointer-fine:min-w-0 disabled:opacity-40');
const FOOT = cn(buttonVariants({ variant: 'ghost', size: 'sm' }), 'h-8 px-2 pointer-fine:min-h-0 pointer-fine:min-w-0');

export function MonthPicker({
  label, value, onChange, min, max, required, disabled, placeholder = 'Chọn tháng', readOnly, id, className, align = 'start',
  'aria-describedby': describedBy, 'aria-invalid': invalid,
}: MonthPickerProps) {
  const current = monthOf(businessDate(new Date()));
  const chosen = MONTH.test(value) ? value : null;
  const isDisabled = (month: string) => (min !== undefined && month < min) || (max !== undefined && month > max);
  /** Keeps a month inside `min`/`max`. */
  const clamp = (month: string) => (min !== undefined && month < min ? min : max !== undefined && month > max ? max : month);
  /** Where the grid's tab stop lands in a year: the chosen month, else this month, else the first one offered. */
  const landing = (year: number): string => {
    if (chosen !== null && parts(chosen)[0] === year && !isDisabled(chosen)) return chosen;
    if (parts(current)[0] === year && !isDisabled(current)) return current;
    return clamp(key(year, 1));
  };

  const [open, setOpen] = useState(false);
  const [year, setYear] = useState(() => parts(chosen ?? current)[0]);
  const [active, setActive] = useState(() => landing(year));
  const [focusRequest, setFocusRequest] = useState(0);
  const text = shown(value);

  const goYear = (next: number) => {
    setYear(next);
    setActive(landing(next));
  };
  const choose = (next: string) => {
    setOpen(false);
    if (next !== value) onChange(next);
  };
  /** A key on the grid; answers whether it used it. */
  const onKey = (pressed: string): boolean => {
    const [, m] = parts(active);
    const col = (m - 1) % 3;
    const delta = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -3, ArrowDown: 3, Home: -col, End: 2 - col }[pressed];
    if (delta === undefined) return false;
    const next = clamp(shift(active, delta));
    setYear(parts(next)[0]);
    setActive(next);
    setFocusRequest((n) => n + 1);
    return true;
  };

  const prevShut = min !== undefined && key(year - 1, 12) < min;
  const nextShut = max !== undefined && key(year + 1, 1) > max;

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (next && (readOnly || disabled)) return;
        if (next) {
          const y = parts(chosen ?? current)[0];
          setYear(y);
          setActive(landing(y));
        }
        setOpen(next);
      }}
    >
      <PopoverTrigger
        id={id} type="button" disabled={disabled} data-field-trigger=""
        aria-label={`${label}: ${text ?? 'chưa chọn'}`}
        aria-readonly={readOnly ? true : undefined}
        aria-describedby={describedBy} aria-invalid={invalid ? true : undefined}
        className={className ?? cn(
          'inline-flex h-11 pointer-fine:h-9 w-full min-w-0 items-center gap-2 rounded-md border border-[hsl(var(--h-border))] bg-transparent px-3 text-left text-sm shadow-xs outline-none',
          'disabled:cursor-not-allowed disabled:opacity-50',
          'aria-invalid:border-destructive',
          readOnly && 'cursor-default bg-[hsl(var(--h-muted))] text-[hsl(var(--h-muted-foreground))]',
        )}
      >
        <CalendarRange aria-hidden="true" className="size-4 shrink-0 text-[hsl(var(--h-muted-foreground))]" />
        <span className={cn('truncate', text === null && 'text-[hsl(var(--h-muted-foreground))]')}>{text ?? placeholder}</span>
      </PopoverTrigger>
      <PopoverContent
        aria-label="Chọn tháng" role="dialog" align={align} className="w-auto p-3"
        // The grid puts the focus on its chosen (or this) month itself.
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <div className="flex w-[252px] flex-col gap-3">
          <div className="flex h-[30px] items-center justify-between">
            <button type="button" aria-label="Năm trước" disabled={prevShut} className={NAV} onClick={() => goYear(year - 1)}>
              <ChevronLeft aria-hidden="true" className="size-4" />
            </button>
            <span aria-live="polite" className="text-sm font-semibold">{pad(year, 4)}</span>
            <button type="button" aria-label="Năm sau" disabled={nextShut} className={NAV} onClick={() => goYear(year + 1)}>
              <ChevronRight aria-hidden="true" className="size-4" />
            </button>
          </div>
          <Suspense fallback={<p className="flex h-[172px] items-center justify-center text-sm text-[hsl(var(--h-muted-foreground))]">Đang tải…</p>}>
            <MonthGrid
              year={year} value={chosen ?? ''} current={current} active={active} focusRequest={focusRequest}
              isDisabled={isDisabled} onKey={onKey} onChoose={choose}
            />
          </Suspense>
        </div>
        <div className="mt-2 flex w-[252px] flex-wrap items-center gap-1 border-t border-[hsl(var(--h-border))] pt-2">
          <button type="button" disabled={isDisabled(current)} title={shown(current) ?? undefined} className={FOOT} onClick={() => choose(current)}>
            Tháng này
          </button>
          {!required && (
            <button type="button" className={cn(FOOT, 'ml-auto')} onClick={() => choose('')}>
              Xoá
            </button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
